import { useRef, useEffect, useState, useCallback, useId } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Eraser, Undo2, Download } from "lucide-react"

interface SignaturePadProps {
  width?: number
  height?: number
  lineWidth?: number
  lineColor?: string
  backgroundColor?: string
  onSignatureChange?: (dataUrl: string | null) => void
  disabled?: boolean
  className?: string
  /** Nom accessible de la toile ; son état (vide / tracée) s'y ajoute. */
  ariaLabel?: string
}

export function SignaturePad({
  width = 400,
  height = 200,
  lineWidth = 2,
  lineColor = "#000000",
  backgroundColor = "#ffffff",
  onSignatureChange,
  disabled = false,
  className,
  ariaLabel = "Zone de signature",
}: SignaturePadProps) {
  const helpId = useId()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isEmpty, setIsEmpty] = useState(true)
  const [historyLength, setHistoryLength] = useState(0)
  const historyRef = useRef<ImageData[]>([])

  const initCanvas = useCallback(() => {
    if (!canvasRef.current) return

    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)
    ctx.lineWidth = lineWidth
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.strokeStyle = lineColor
  }, [backgroundColor, height, lineColor, lineWidth, width])

  useEffect(() => {
    initCanvas()
  }, [initCanvas])

  const saveState = () => {
    if (!canvasRef.current) return
    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return
    historyRef.current.push(ctx.getImageData(0, 0, width, height))
    if (historyRef.current.length > 50) {
      historyRef.current.shift()
    }
    setHistoryLength(historyRef.current.length)
  }

  const getPosition = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): { x: number; y: number } => {
    if (!canvasRef.current) return { x: 0, y: 0 }

    const rect = canvasRef.current.getBoundingClientRect()
    const scaleX = canvasRef.current.width / rect.width
    const scaleY = canvasRef.current.height / rect.height

    if ("touches" in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      }
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (disabled) return
    saveState()

    const ctx = canvasRef.current?.getContext("2d")
    if (!ctx) return

    const { x, y } = getPosition(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    setIsDrawing(true)
  }

  const draw = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (!isDrawing || disabled) return

    const ctx = canvasRef.current?.getContext("2d")
    if (!ctx) return

    const { x, y } = getPosition(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    setIsEmpty(false)
  }

  const stopDrawing = () => {
    if (!isDrawing) return
    setIsDrawing(false)
    notifyChange()
  }

  const notifyChange = () => {
    if (!canvasRef.current) return
    const dataUrl = isEmpty ? null : canvasRef.current.toDataURL("image/png")
    onSignatureChange?.(dataUrl)
  }

  const clear = () => {
    if (!canvasRef.current) return
    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    saveState()
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)
    ctx.strokeStyle = lineColor
    setIsEmpty(true)
    onSignatureChange?.(null)
  }

  const undo = () => {
    if (historyRef.current.length === 0) return

    const ctx = canvasRef.current?.getContext("2d")
    if (!ctx) return

    const previousState = historyRef.current.pop()
    setHistoryLength(historyRef.current.length)
    if (previousState) {
      ctx.putImageData(previousState, 0, 0)
      notifyChange()
    }
  }

  const download = () => {
    if (!canvasRef.current || isEmpty) return

    const link = document.createElement("a")
    link.download = "signature.png"
    link.href = canvasRef.current.toDataURL("image/png")
    link.click()
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div
        className={cn(
          "border-2 border-dashed rounded-lg overflow-hidden",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          role="img"
          aria-label={`${ariaLabel} : ${isEmpty ? "vide" : "signature tracée"}`}
          aria-describedby={helpId}
          data-test="signature-pad-canvas"
          className={cn(
            "w-full touch-none cursor-crosshair",
            disabled && "pointer-events-none"
          )}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={undo}
            disabled={disabled || historyLength === 0}
          >
            <Undo2 className="h-4 w-4 mr-1" />
            Annuler
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clear}
            disabled={disabled || isEmpty}
          >
            <Eraser className="h-4 w-4 mr-1" />
            Effacer
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={download}
          disabled={isEmpty}
        >
          <Download className="h-4 w-4 mr-1" />
          Télécharger
        </Button>
      </div>
      <p id={helpId} className="text-xs text-muted-foreground text-center">
        Tracez la signature dans le cadre ci-dessus, au doigt, au stylet ou à la souris.
      </p>
    </div>
  )
}

// Export a function to get signature data from component ref
SignaturePad.displayName = "SignaturePad"
